<template>
  <!-- 销售目标：年度/季度/月度目标 + 实时达成统计 -->
  <div class="sale-target">
    <list-page>
      <search-condition @search="queryAll" page-name="saleTargetList">
        <template #default>
          <el-select v-model="query.targetYear" placeholder="年度">
            <template #prefix>年度</template>
            <el-option v-for="y in yearOptions" :key="y" :label="y" :value="y"/>
          </el-select>
          <employee-select v-model="query.employeeId" placeholder="全部销售人员" :disabled="!isAdmin">
            <template #prefix>销售人员</template>
          </employee-select>
          <el-select v-model="query.targetType" placeholder="全部目标维度" clearable>
            <template #prefix>目标维度</template>
            <el-option label="年度目标" :value="1"/>
            <el-option label="季度目标" :value="2"/>
            <el-option label="月度目标" :value="3"/>
          </el-select>
          <el-select v-model="query.businessType" placeholder="全部业务类型" clearable>
            <template #prefix>业务类型</template>
            <el-option label="毛利" :value="1"/>
            <el-option label="新增客户" :value="2"/>
            <el-option label="客户转介绍" :value="3"/>
            <el-option label="服务费" :value="4"/>
          </el-select>
        </template>
        <template #option>
          <page-button type="read" el-type="primary" @click="queryAll">查询</page-button>
          <page-button type="write" el-type="success" @click="openDialog()">新建目标</page-button>
        </template>
      </search-condition>

      <list-table background-color="#FFF">
        <yq-table :data="dataList" height="100%" stripe border page-name="saleTargetList" @rowClick="onRowClick">
          <yq-table-column label="目标编号" prop="targetNo" min-width="150rem"/>
          <yq-table-column label="目标人" prop="employeeName" min-width="110rem">
            <template v-slot="{row}">{{ row.employeeName || '—' }}</template>
          </yq-table-column>
          <yq-table-column label="目标名称" prop="targetName" min-width="140rem"/>
          <yq-table-column label="时间维度" prop="targetTypeName" width="110rem" align="center">
            <template v-slot="{row}">
              <el-tag size="small" :type="targetTypeTag(row.targetType)">{{ row.targetTypeName }}</el-tag>
            </template>
          </yq-table-column>
          <yq-table-column label="业务类型" prop="businessTypeName" width="120rem" align="center"/>
          <yq-table-column label="目标值" prop="targetAmount" width="130rem" align="right">
            <template v-slot="{row}">{{ metric(row.targetAmount, row.unit) }}<span class="muted">{{ row.unit }}</span></template>
          </yq-table-column>
          <yq-table-column label="已达成" prop="achievedAmount" width="130rem" align="right">
            <template v-slot="{row}">{{ metric(row.achievedAmount, row.unit) }}<span class="muted">{{ row.unit }}</span></template>
          </yq-table-column>
          <yq-table-column label="达成率" prop="achievementRate" width="160rem" align="center">
            <template v-slot="{row}">
              <div class="rate-cell">
                <el-progress :percentage="ratePercent(row.achievementRate)" :stroke-width="8"
                             :show-text="false" :color="rateColor(row.achievementRate)"/>
                <span class="rate-text">{{ row.achievementRate || 0 }}%</span>
              </div>
            </template>
          </yq-table-column>
          <yq-table-column label="编制人" prop="creatorName" width="110rem" align="center">
            <template v-slot="{row}">{{ row.creatorName || '—' }}</template>
          </yq-table-column>
          <yq-table-column label="备注" prop="remark" min-width="180rem" :show-overflow-tooltip="true">
            <template v-slot="{row}">{{ row.remark || '—' }}</template>
          </yq-table-column>
          <yq-table-column label="操作" prop="options" align="center" fixed="right" width="140rem" :show-overflow-tooltip="false">
            <template v-slot="{row}">
              <table-button @click="openDialog(row)">编辑</table-button>
              <table-button type="delete" el-type="danger" message="删除年度/季度目标会同时删除其下级目标，是否继续"
                            @click="deleteTarget(row)">删除</table-button>
            </template>
          </yq-table-column>
        </yq-table>
      </list-table>
      <form-page v-model:page-number="query.pageNumber" v-model:page-size="query.pageSize"
                 :total-count="query.totalCount" @onLoad="queryList"/>
    </list-page>

    <!-- 新建/编辑目标 -->
    <el-dialog v-model="showDialog" :title="dialogData.id ? '编辑销售目标' : '新建销售目标'" dialog-width="2"
               :close-on-click-modal="false">
      <form-items ref="dialogForm" :data="dialogData" :columns="2" label-width="2.4rem" icon-width="0">
        <form-item label="目标人" :required="true" name="employeeId" error-message="请选择目标人">
          <employee-select v-model="dialogData.employeeId" :disabled="!!dialogData.id || !isAdmin" placeholder="请选择目标人"/>
        </form-item>
        <form-item label="目标年度" :required="true" name="targetYear" error-message="请选择目标年度">
          <el-select v-model="dialogData.targetYear" :disabled="!!dialogData.id" style="width: 100%">
            <el-option v-for="y in yearOptions" :key="y" :label="y" :value="y"/>
          </el-select>
        </form-item>
        <form-item label="时间维度" :required="true" name="targetType" error-message="请选择时间维度">
          <el-select v-model="dialogData.targetType" :disabled="!!dialogData.id" style="width: 100%"
                     @change="onTargetTypeChange">
            <el-option label="年度目标" :value="1"/>
            <el-option label="季度目标" :value="2"/>
            <el-option label="月度目标" :value="3"/>
          </el-select>
        </form-item>
        <form-item v-if="dialogData.targetType === 2" label="季度" :required="true" name="targetQuarter" error-message="请选择季度">
          <el-select v-model="dialogData.targetQuarter" :disabled="!!dialogData.id" style="width: 100%">
            <el-option v-for="q in 4" :key="q" :label="'第' + quarterText(q) + '季度'" :value="q"/>
          </el-select>
        </form-item>
        <form-item v-if="dialogData.targetType === 3" label="月份" :required="true" name="targetMonth" error-message="请选择月份">
          <el-select v-model="dialogData.targetMonth" :disabled="!!dialogData.id" style="width: 100%">
            <el-option v-for="m in 12" :key="m" :label="m + '月'" :value="m"/>
          </el-select>
        </form-item>
        <form-item label="业务类型" :required="true" name="businessType" error-message="请选择业务类型">
          <el-select v-model="dialogData.businessType" :disabled="!!dialogData.id" style="width: 100%">
            <el-option label="毛利" :value="1"/>
            <el-option label="新增客户" :value="2"/>
            <el-option label="客户转介绍" :value="3"/>
            <el-option label="服务费" :value="4"/>
          </el-select>
        </form-item>
        <form-item label="目标值" :required="true" name="targetAmount" error-message="请填写目标值">
          <el-input-number v-model="dialogData.targetAmount" :min="0" :precision="dialogPrecision"
                           controls-position="right" style="width: 100%"/>
        </form-item>
        <form-item label="备注" :show-block="true">
          <el-input v-model="dialogData.remark" type="textarea" :rows="2" placeholder="备注信息"/>
        </form-item>
      </form-items>
      <div class="dialog-tip">新建年度目标会自动按季度、月度平均拆分；已存在的季度/月度目标不会被重复添加。</div>
      <template #footer>
        <page-button @click="showDialog = false">取消</page-button>
        <page-button type="write" el-type="primary" @click="saveTarget">保存</page-button>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import ListPage from "@/components/h5/layout/ListPage.vue";
import ListTable from "@/components/h5/layout/ListTable.vue";
import SearchCondition from "@/components/h5/layout/SearchCondition.vue";
import FormPage from "@/components/h5/layout/FormPage.vue";
import FormItems from "@/components/h5/layout/FormItems.vue";
import FormItem from "@/components/h5/layout/FormItem.vue";
import PageButton from "@/components/h5/button/PageButton.vue";
import TableButton from "@/components/h5/button/TableButton.vue";
import EmployeeSelect from "@/components/h5/data/EmployeeSelect.vue";
import saleTargetApi from "@/api/dz-crm/sale/saleTargetApi";
import Util from "@/util/util";

const emptyDialog = (employeeId) => ({
  id: "", employeeId: employeeId || "", targetYear: new Date().getFullYear(),
  targetType: 1, targetQuarter: 1, targetMonth: 1, businessType: 1,
  targetAmount: 0, remark: "",
});

export default {
  name: "SaleTargetList",
  components: {
    EmployeeSelect, TableButton, PageButton, FormItems, FormItem,
    SearchCondition, FormPage, ListTable, ListPage,
  },
  data() {
    const year = new Date().getFullYear();
    const isAdmin = Number(Util.getRoleType()) === 1;
    return {
      isAdmin,
      currentEmployeeId: Util.getEmployeeId(),
      yearOptions: Array.from({length: 7}, (_, i) => year - 5 + i),
      // 超级管理员默认不选人（查询全部），普通角色锁死本人
      query: {
        targetYear: year,
        employeeId: isAdmin ? "" : Util.getEmployeeId(),
        targetType: null,
        businessType: null,
        pageNumber: 1,
        pageSize: 20,
      },
      dataList: [],
      showDialog: false,
      dialogData: emptyDialog(Util.getEmployeeId()),
    };
  },
  computed: {
    dialogPrecision() {
      return this.dialogData.businessType === 2 || this.dialogData.businessType === 3 ? 0 : 2;
    },
  },
  created() {
    const currentYear = new Date().getFullYear();
    if (!this.query.targetYear) {
      this.query.targetYear = currentYear;
    }
    if (!this.isAdmin) {
      this.query.employeeId = this.currentEmployeeId;
    }
    this.queryAll();
  },
  methods: {
    queryAll() {
      // 普通角色锁死本人；超级管理员不选人时查询全部数据
      if (!this.isAdmin) {
        this.query.employeeId = this.currentEmployeeId;
      }
      this.query.pageNumber = 1;
      this.queryList();
    },
    queryList() {
      saleTargetApi.queryList(this.query).then(res => {
        this.dataList = res.records || [];
        this.query.totalCount = res.totalCount || 0;
      });
    },
    // ---------- 新建/编辑 ----------
    openDialog(row) {
      if (row) {
        this.dialogData = {
          id: row.id,
          employeeId: row.employeeId,
          targetYear: row.targetYear,
          targetType: row.targetType,
          targetQuarter: row.targetQuarter || 1,
          targetMonth: row.targetMonth || 1,
          businessType: row.businessType,
          targetAmount: Number(row.targetAmount || 0),
          remark: row.remark || "",
        };
      } else {
        this.dialogData = emptyDialog(this.query.employeeId || this.currentEmployeeId);
        this.dialogData.targetYear = this.query.targetYear || new Date().getFullYear();
      }
      this.showDialog = true;
    },
    onTargetTypeChange() {
      if (this.dialogData.targetType === 2 && !this.dialogData.targetQuarter) {
        this.dialogData.targetQuarter = 1;
      }
      if (this.dialogData.targetType === 3 && !this.dialogData.targetMonth) {
        this.dialogData.targetMonth = 1;
      }
    },
    saveTarget() {
      if (!this.$refs.dialogForm || !this.$refs.dialogForm.validCheck()) {
        return;
      }
      const data = {...this.dialogData};
      if (!data.employeeId) {
        this.$message.error("请选择目标人");
        return;
      }
      if (Number(data.targetAmount) < 0) {
        this.$message.error("目标值不能小于0");
        return;
      }
      saleTargetApi.save(data).then(() => {
        this.$message.success("保存成功");
        this.showDialog = false;
        // 管理员新建他人目标后，把查询切到该目标人，方便直接看到结果
        if (this.isAdmin && data.employeeId !== this.query.employeeId) {
          this.query.employeeId = data.employeeId;
        }
        this.queryAll();
      });
    },
    deleteTarget(row) {
      saleTargetApi.delete(row.id).then(() => {
        this.$message.success("删除成功");
        this.queryAll();
      });
    },
    // ---------- 跳转合同 ----------
    /**
     * 点击目标行：按目标的销售人员与周期跳到合同列表。
     * 操作列里的按钮不触发跳转（rowClick 是整行事件，按钮点击也会冒上来）。
     */
    onRowClick(row, column) {
      if (!column || "options" === column.property) {
        return;
      }
      if (!row.employeeId) {
        return;
      }
      const range = this.targetRange(row);
      this.$router.push({
        path: "/contract/list",
        query: {salesId: row.employeeId, startTime: range.start, endTime: range.end},
      });
    },
    /** 目标周期换算成签约日期区间：年度整年 / 季度三个月 / 月份当月 */
    targetRange(row) {
      const pad = n => String(n).padStart(2, "0");
      const lastDay = (year, month) => new Date(year, month, 0).getDate();
      const year = Number(row.targetYear) || new Date().getFullYear();
      if (row.targetType === 2) {
        const quarter = Number(row.targetQuarter) || 1;
        const startMonth = quarter * 3 - 2;
        const endMonth = quarter * 3;
        return {
          start: `${year}-${pad(startMonth)}-01`,
          end: `${year}-${pad(endMonth)}-${pad(lastDay(year, endMonth))}`,
        };
      }
      if (row.targetType === 3) {
        const month = Number(row.targetMonth) || 1;
        return {
          start: `${year}-${pad(month)}-01`,
          end: `${year}-${pad(month)}-${pad(lastDay(year, month))}`,
        };
      }
      return {start: `${year}-01-01`, end: `${year}-12-31`};
    },
    // el-progress 只接受 0-100，超过 100% 时进度条按满格显示，文本仍展示真实值
    ratePercent(rate) {
      return Math.max(0, Math.min(100, Number(rate) || 0));
    },
    rateColor(rate) {
      const value = Number(rate) || 0;
      if (value >= 100) return "#67c23a";
      if (value >= 60) return "#409eff";
      return "#e6a23c";
    },
    // ---------- 展示 ----------
    metric(value, unit) {
      if (value === null || value === undefined || value === "") {
        return unit === "个" ? "0" : "0.00";
      }
      return Number(value).toLocaleString("zh-CN", {
        minimumFractionDigits: unit === "个" ? 0 : 2,
        maximumFractionDigits: unit === "个" ? 0 : 2,
      });
    },
    targetTypeTag(type) {
      if (type === 1) return "primary";
      if (type === 2) return "success";
      return "warning";
    },
    quarterText(q) {
      return ["一", "二", "三", "四"][Number(q) - 1] || "";
    },
  },
};
</script>

<style lang="scss" scoped>
.sale-target {
  height: 100%;
}
.muted {
  margin-left: 4px;
  font-size: 12px;
  color: #909399;
}

.rate-cell {
  display: flex;
  align-items: center;
  gap: 8px;

  :deep(.el-progress) {
    flex: 1;
    min-width: 0;
  }

  .rate-text {
    flex: none;
    min-width: 44px;
    text-align: right;
    font-size: 12px;
    color: var(--yq-font-color-desc);
    font-variant-numeric: tabular-nums;
  }
}

.dialog-tip {
  padding: 4px 16px 0;
  font-size: 12px;
  color: #909399;
}
</style>
